import type { Difficulty, GameState, GameStatus } from './types';
import { DIFFICULTY_SETTINGS, getRandomWord } from './types';

export function createGame(difficulty: Difficulty, category?: string): GameState {
  const settings = DIFFICULTY_SETTINGS[difficulty];
  return {
    word: getRandomWord(category),
    guessedLetters: new Set(),
    wrongGuesses: 0,
    maxWrongGuesses: settings.maxWrong,
    status: 'playing',
    difficulty,
    score: 0,
    gamesPlayed: 0,
    gamesWon: 0,
  };
}

export function nextGame(prev: GameState, difficulty: Difficulty, category?: string): GameState {
  const settings = DIFFICULTY_SETTINGS[difficulty];
  return {
    word: getRandomWord(category),
    guessedLetters: new Set(),
    wrongGuesses: 0,
    maxWrongGuesses: settings.maxWrong,
    status: 'playing',
    difficulty,
    score: prev.score,
    gamesPlayed: prev.gamesPlayed + 1,
    gamesWon: prev.gamesWon,
  };
}

export function makeGuess(state: GameState, letter: string): GameState {
  if (state.status !== 'playing') return state;
  if (state.guessedLetters.has(letter)) return state;

  const newGuessed = new Set(state.guessedLetters);
  newGuessed.add(letter);

  const isCorrect = state.word.includes(letter);
  const newWrong = isCorrect ? state.wrongGuesses : state.wrongGuesses + 1;

  // Check win
  const allLettersGuessed = state.word
    .split('')
    .every((l) => newGuessed.has(l));

  let status: GameStatus = 'playing';
  let score = state.score;
  if (allLettersGuessed) {
    status = 'won';
    // Award points based on difficulty and remaining wrong guesses
    const remaining = state.maxWrongGuesses - state.wrongGuesses;
    const difficultyMultiplier = state.difficulty === 'easy' ? 1 : state.difficulty === 'medium' ? 2 : 3;
    score += (remaining + 1) * 10 * difficultyMultiplier;
  } else if (newWrong >= state.maxWrongGuesses) {
    status = 'lost';
  }

  return {
    ...state,
    guessedLetters: newGuessed,
    wrongGuesses: newWrong,
    status,
    score,
  };
}

export function revealWord(state: GameState): string {
  return state.word
    .split('')
    .map((letter) => (state.guessedLetters.has(letter) ? letter : '_'))
    .join(' ');
}

export function getLetterState(
  letter: string,
  word: string,
  guessedLetters: Set<string>,
): 'unused' | 'correct' | 'wrong' {
  if (!guessedLetters.has(letter)) return 'unused';
  return word.includes(letter) ? 'correct' : 'wrong';
}
